// injected-evm-wallet-types.ts — tiny, dependency-free surface for the EVM
// WALLET LOGIN path (BYO wallet via SIWE). The EVM twin of
// injected-wallet-types.ts: kept separate from injected-evm-wallet-provider.ts
// so the SDK entry can eagerly export the type surface + the provider-discovery
// helper WITHOUT dragging the provider class into the default bundle. The
// provider is lazy-loaded by getAuthProvider() when an app opts into
// authMethod:'evm-wallet'.
//
// It rides the standard EIP-1193 injected provider a browser wallet exposes
// (MetaMask / Rabby / Coinbase Wallet / any window.ethereum), discovered via
// EIP-6963 (multi-wallet, collision-free) with a window.ethereum fallback. It
// needs NO viem/ethers and NO React — EIP-1193 request() + hex strings suffice.
// EIP-6963 is announcement-based: wallets add an `eip6963:requestProvider`
// listener and synchronously dispatch `eip6963:announceProvider` in response. We
// keep a persistent collector so announcements (including late ones) are
// captured, and re-request on demand.
const announcedProviders = [];
let announceListenerAttached = false;
function attachAnnounceListener() {
    if (announceListenerAttached || typeof window === 'undefined')
        return;
    announceListenerAttached = true;
    window.addEventListener('eip6963:announceProvider', (event) => {
        const detail = event === null || event === void 0 ? void 0 : event.detail;
        if (detail &&
            detail.provider &&
            typeof detail.provider.request === 'function' &&
            !announcedProviders.some((d) => { var _a, _b; return ((_a = d.info) === null || _a === void 0 ? void 0 : _a.uuid) === ((_b = detail.info) === null || _b === void 0 ? void 0 : _b.uuid); })) {
            announcedProviders.push(detail);
        }
    });
}
/**
 * Request + collect EIP-6963 providers. Dispatches `eip6963:requestProvider`
 * (synchronous — already-loaded wallets announce in the same tick) and returns
 * every wallet announced so far. Kept synchronous so it works inside a user
 * gesture and can be eagerly exported from the SDK entry.
 */
export function requestEip6963Providers() {
    if (typeof window === 'undefined')
        return [];
    attachAnnounceListener();
    try {
        window.dispatchEvent(new Event('eip6963:requestProvider'));
    }
    catch (_a) {
        /* older browsers without Event constructor — fall through to window.ethereum */
    }
    return announcedProviders.slice();
}
/**
 * Default EVM provider discovery: EIP-6963 first (multi-wallet, collision-free),
 * then legacy `window.ethereum`. Returns null when no injected wallet is present.
 */
export function defaultInjectedEvmProvider() {
    if (typeof window === 'undefined')
        return null;
    const details = requestEip6963Providers();
    if (details.length > 0)
        return details[0].provider;
    const eth = window.ethereum;
    if (eth && typeof eth.request === 'function')
        return eth;
    return null;
}
